import { FormFieldGroup, FormInput, FormLabel, FormTextarea, FormLabelDescription, FormSelect } from '$components'
import { Currency, DogBreed } from '$api-client/types.gen'
import { ColourInput } from '../../components/colour-input'
import { CreateServiceSchema, Input } from './'
import { useFormContext } from 'react-hook-form'
import { PricingVariantList } from '../../components/pricing-variant-list'
import { useInfiniteSizeCategoryOptions } from '$hooks/use-infinite-size-category-options'
import { useInfiniteCoatTypeOptions } from '$hooks/use-infinite-coat-type-options'

export function CreateServiceContent() {
  const { watch, setValue, formState: { errors } } = useFormContext<CreateServiceSchema>()

  const variants = watch(Input.Variants) || []
  const currency = watch(Input.Currency)

  const { options: sizeOptions } = useInfiniteSizeCategoryOptions()
  const { options: coatOptions } = useInfiniteCoatTypeOptions()
  const breedOptions = Object.entries(DogBreed).map(([id, value]) => ({ id, value }))

  const addVariant = (type: 'size-coat' | 'breed') => {
    const newVariant = {
      breed: null,
      size_category: null,
      coat_type: null,
      override_type: type,
      is_default: false,
      price: 30,
      duration: 30,
    }
    setValue(Input.Variants, [...variants, newVariant])
  }

  const removeVariant = (index: number) => {
    setValue(Input.Variants, variants.filter((_, i) => i !== index))
  }

  const updateVariant = (index: number, field: string, value: unknown) => {
    const updated = [...variants]
    updated[index] = { ...updated[index], [field]: value }
    setValue(Input.Variants, updated)
  }

  return (
    <div className="flex flex-col gap-6 flex-grow">
      <FormFieldGroup>
        <FormLabel htmlFor={Input.Name}>
          Name
        </FormLabel>

        <FormLabelDescription>
          Give your service a clear name that your team and clients will easily understand.
        </FormLabelDescription>

        <FormInput
          name={Input.Name}
          placeholder="Enter service name..."
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.Description}>
          Description
        </FormLabel>

        <FormLabelDescription>
          Describe what&apos;s included in this service. (This will be visible to your clients).
        </FormLabelDescription>

        <FormTextarea
          name={Input.Description}
          placeholder="Enter service description..."
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.Colour}>
          Colour
        </FormLabel>

        <FormLabelDescription>
          Choose a colour to help identify this service in your calendar.
        </FormLabelDescription>

        <ColourInput
          name={Input.Colour}
          error={errors[Input.Colour]?.message}
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.Currency}>
          Currency
        </FormLabel>

        <FormLabelDescription>
          Choose what currency the cost of this service is in.
        </FormLabelDescription>

        <FormSelect
          name={Input.Currency}
          placeholder="Select currency..."
          options={Object.values(Currency).map((currency) => ({
            id: currency,
            value: currency,
          }))}
        />
      </FormFieldGroup>

      <PricingVariantList
        variants={variants}
        fieldName={Input.Variants}
        currency={currency?.id}
        sizeOptions={sizeOptions}
        coatOptions={coatOptions}
        breedOptions={breedOptions}
        onAdd={addVariant}
        onRemove={removeVariant}
        onDurationChange={(index, duration) => updateVariant(index, 'duration', duration)}
        error={errors[Input.Variants]?.message}
      />

      <FormFieldGroup>
        <FormLabel htmlFor={Input.Notes}>
          Internal Notes
        </FormLabel>

        <FormLabelDescription>
          Add any internal notes about this service (only visible to you and your team).
        </FormLabelDescription>

        <FormTextarea
          name={Input.Notes}
          placeholder="Your notes"
        />
      </FormFieldGroup>
    </div>
  )
}
